import React, { createContext, useContext, useMemo } from "react";
import { User as FirebaseUser } from 'firebase/auth';
import type { AppSocketContextValue, IncomingMove, LeaderboardRequest, LobbyUpdateData, NextRoundData, CancelledTournament } from '@/shared/types';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import useAppSocketRedux from "./useAppSocketRedux";
import {
  selectCurrentGame,
  selectCancelledTournament,
  selectNextRoundMessage,
  clearCancelledTournament,
  clearNextRoundMessage,
} from '@/store/slices/gameSessionSlice';
import {
  selectLatestIncomingMove,
  selectLatestIncomingTime,
  clearLatestIncomingMove
} from '@/store/slices/movesSlice';
import {
  selectLatestLeaderboardRequest,
  clearLatestLeaderboardRequest
} from '@/store/slices/leaderboardSlice';
import {
  selectLobbyUpdateData,
  clearLobbyUpdateData
} from '@/store/slices/tournamentsSlice';

const AppSocketContext = createContext<AppSocketContextValue | undefined>(undefined);

export function AppSocketProvider({ user, children }: { user: FirebaseUser | null; children: React.ReactNode }) {
  const dispatch = useAppDispatch();
  const { isConnected, reconnectCountdown } = useAppSocketRedux(user);

  const cancelledTournament = useAppSelector(selectCancelledTournament);
  const gameData = useAppSelector(selectCurrentGame);
  const incomingMove = useAppSelector(selectLatestIncomingMove);
  const incomingTime = useAppSelector(selectLatestIncomingTime);
  const updatedLeaderboardRequest = useAppSelector(selectLatestLeaderboardRequest);
  const nextRoundMessage = useAppSelector(selectNextRoundMessage);
  const lobbyUpdateData = useAppSelector(selectLobbyUpdateData);

  const value = useMemo<AppSocketContextValue>(() => ({
    isConnected,
    reconnectCountdown,
    cancelledTournament,
    setCancelledTournament: (cancel: CancelledTournament | null) => {
      if (cancel === null) {
        dispatch(clearCancelledTournament());
      }
    },
    gameData,
    incomingMove,
    setIncomingMove: (move: IncomingMove | null) => {
      if (move === null) {
        dispatch(clearLatestIncomingMove());
      }
    },
    incomingTime,
    updatedLeaderboardRequest,
    setLeaderboardRequest: (request: LeaderboardRequest | null) => {
      if (request === null) {
        dispatch(clearLatestLeaderboardRequest());
      }
    },
    nextRoundMessage,
    setNextRoundMessage: (nextRound: NextRoundData | null) => {
      if (nextRound === null) {
        dispatch(clearNextRoundMessage());
      }
    },
    lobbyUpdateData,
    setLobbyUpdateData: (lobbyUpdate: LobbyUpdateData | null) => {
      if (lobbyUpdate === null) {
        dispatch(clearLobbyUpdateData());
      }
    }
  }), [
    isConnected,
    reconnectCountdown,
    cancelledTournament,
    gameData,
    incomingMove,
    incomingTime,
    updatedLeaderboardRequest,
    nextRoundMessage,
    lobbyUpdateData,
    dispatch
  ]);

  return (
    <AppSocketContext.Provider value={value}>
      {children}
    </AppSocketContext.Provider>
  );
}

export function useAppSocketContext() {
  const context = useContext(AppSocketContext);
  if (!context) throw new Error("useAppSocketContext must be used within an AppSocketProvider");
  return context;
}
